/**
 * Customer Identity, Address & Storefront Modal Reliability — Phase 7.
 *
 * Thin client for `POST /api/retail/public/{slug}/audit-event` (see
 * `backend/retail/customer_audit.py` for the closed event vocabulary
 * and the masking every write goes through server-side). This helper
 * additionally never sends a raw phone/email itself — it masks
 * client-side too, so the value in transit is already reduced, not just
 * reduced at rest.
 *
 * Fire-and-forget by design: an audit beacon must never block or fail
 * a customer-facing action, so every call swallows its own errors.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function maskPhoneForAudit(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length < 4) return undefined;
  return `***-***-${digits.slice(-4)}`;
}

function maskEmailForAudit(raw) {
  const value = String(raw || "").trim();
  const at = value.indexOf("@");
  if (at < 1) return undefined;
  const user = value.slice(0, at);
  const domain = value.slice(at + 1);
  const head = user.slice(0, 2);
  return `${head}${"•".repeat(Math.max(1, user.length - head.length))}@${domain}`;
}

export function logCustomerAuditEvent(slug, {
  event, reasonCode, orderType, catalogId, sessionId, customerId, phone, email, meta,
} = {}) {
  if (!slug || !event) return;
  try {
    axios.post(
      `${BACKEND_URL}/api/retail/public/${encodeURIComponent(slug)}/audit-event`,
      {
        event,
        reason_code: reasonCode || null,
        order_type: orderType || null,
        // PR #41 hardening (Phase 5) — complete the stable envelope
        // (company_id/event/reason_code/timestamp already always
        // present) with the catalog/session/customer correlation
        // fields every significant workflow event should carry.
        catalog_id: catalogId || null,
        session_id: sessionId || null,
        customer_id: customerId || null,
        // Already-masked client-side; the backend re-masks/validates
        // defensively and never trusts a client-supplied value blindly.
        phone: phone ? maskPhoneForAudit(phone) : undefined,
        email: email ? maskEmailForAudit(email) : undefined,
        meta: meta || undefined,
      },
      { timeout: 5000 },
    ).catch(() => { /* best-effort — never surface an audit failure to the customer */ });
  } catch (_e) {
    // Synchronous failure (e.g. axios misconfigured) — still never throw.
  }
}
